import { Fade, useTheme } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useEffect, useMemo, useState } from 'react';
import { AddButton, EditableBloc, FlexContainer, IconTitle, Loader, VerticalLine } from '../../components';
import AcceptanceTermsType from '../../entities/acceptance-term-type';
import AcceptanceTerm from '../../entities/acceptance-term.entity';
import { useFetch, useStorage } from '../../hooks';
import useTerms from '../../hooks/data/useTerms';
import NoContent from './components/NoContent';
import TermPanel from './components/TermPanel';
import useStyles from './styles';

const typeMapper: Record<AcceptanceTermsType, string> = {
  [AcceptanceTermsType.AppPrivacyTerms]: 'Termos de Privacidade',
  [AcceptanceTermsType.AppUseTerms]: 'Termos de Uso',
  [AcceptanceTermsType.Laundry]: 'Termos de Lavanderia',
  [AcceptanceTermsType.Cleaning]: 'Termos de Limpeza',
  [AcceptanceTermsType.Maid]: 'Termos de Camareira',
  [AcceptanceTermsType.Market]: 'Termos de Mercado',
  [AcceptanceTermsType.Furniture]: 'Termos de Móveis',
};

export const Terms = (): JSX.Element => {
  const styles = useStyles();
  const { spacing } = useTheme();
  const storage = useStorage();
  const repository = useTerms();
  const { enqueueSnackbar } = useSnackbar();
  const [terms, setTerms] = useState<AcceptanceTerm[]>([]);
  const [selectedTerm, setSelectedTerm] = useState<AcceptanceTerm>({} as AcceptanceTerm);
  const [isPanelVisible, setIsPanelVisible] = useState(false);
  const [fileToUpload, setFileToUpload] = useState<File | null>(null);
  const { data, refetch, loading } = useFetch(repository.fetchAll);
  const [isLoading, setLoading] = useState(false);

  useEffect(() => {
    setTerms(data ?? []);
  }, [data]);

  const handleSetPanelOpen = () => {
    setSelectedTerm({} as AcceptanceTerm);
    setFileToUpload(null);
    setIsPanelVisible(!isPanelVisible);
  };

  const handleUploadImage = async (term: AcceptanceTerm): Promise<string> => {
    if (!fileToUpload) {
      throw new Error();
    }

    const imageUrl = await storage.upload(`/terms/${term.type}-${term.version}-${fileToUpload.name}`, fileToUpload);

    return imageUrl;
  };

  const handleSubmit = async (term: AcceptanceTerm) => {
    setLoading(true);
    const imageUrl = await handleUploadImage(term);

    try {
      await repository.insert({ ...term, url: imageUrl });

      await refetch();

      handleSetPanelOpen();
      enqueueSnackbar('Termo salvo com sucesso!', { variant: 'success' });
    } catch (err) {
      await storage.remove(imageUrl);

      enqueueSnackbar('Não foi possível criar um termo agora', { variant: 'error' });
    }

    setLoading(false);
  };

  const handleUpload = (file: File | null) => {
    setFileToUpload(file);
  };

  const handleSelectTerm = (term?: AcceptanceTerm) => {
    if (term) {
      setSelectedTerm(term);
    }

    setIsPanelVisible(true);
  };

  const showNoContent = useMemo(() => !isPanelVisible && terms.length === 0, [isPanelVisible, terms]);

  if (loading && !data) {
    return (
      <div className={styles.container}>
        <FlexContainer
          sx={{
            justifyContent: 'center',
            alignContent: 'center',
          }}
        >
          <Loader />
        </FlexContainer>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {showNoContent ? (
        <NoContent />
      ) : (
        <div className={styles.containerRight}>
          <IconTitle title="Lista de termos" />
          <FlexContainer>
            {terms.map((term) => (
              <EditableBloc<AcceptanceTerm>
                key={term.id}
                label={`#Termo-${term.id}`}
                value={term}
                width={`calc(25% - ${spacing(1.5)})`}
                maxWidth="600px"
                minWidth="250px"
                onClickEdit={handleSelectTerm}
              >
                <div className={styles.info}>
                  <div>Tipo do termo: {typeMapper[term.type]}</div>
                  <div>Versão: {term.version}</div>
                </div>
              </EditableBloc>
            ))}
          </FlexContainer>
        </div>
      )}
      <Fade in={isPanelVisible} mountOnEnter unmountOnExit>
        <VerticalLine />
      </Fade>
      <TermPanel
        visible={isPanelVisible}
        term={selectedTerm}
        file={fileToUpload}
        loading={isLoading}
        handleUpload={handleUpload}
        onClickClose={handleSetPanelOpen}
        onSubmit={handleSubmit}
      />
      {!isPanelVisible && <AddButton label="adicionar termo" onClick={() => handleSetPanelOpen()} />}
    </div>
  );
};

export default Terms;
